/* Экраны «Текста» (APP-SPEC-5 §7): «Текст из аудио и видео», «Описание товара», «Промпт из фото / видео», «Создать промпт», «Улучшить промпт».
   Порядок блоков §1.1: шапка с названием и ценой за единицу → рамка или главное поле → лента → поле → «Ещё настройки» → кнопка.
   Каждый контрол — по полю формы (fields / screen_fields) или флагу политики; в заказ уходят только опубликованные ключи.
   У промптов «Создать» и «Улучшить» варианты встают на месте (без страницы заказа), у остальных — общий хвост OrderTail. */
import { useCallback, useState, type SetStateAction } from 'react';
import type { SpeechOrder } from '../../../api/speech';
import type { UserAvatar } from '../../../api/people';
import { uiText } from '../../../i18n';
import { studioExpectedPrice, type StudioApp } from '../../studio/studio-api';
import { useStudioForm } from '../../studio/use-studio-form';
import type { CircleFormState } from '../../studio/circle/CircleFieldInput';
import { ToolCreateButton } from '../../studio/circle/ToolCreateButton';
import { ToolHeader } from '../../studio/circle/ToolHeader';
import { leaveTail, toolPrice, unitText, unitWords, useTailRetry } from '../../studio/circle/tool-helpers';
import { toolField, toolFields } from '../../studio/circle/tool-form';
import { record } from '../../studio/studio-content';
import {
  choiceLabels,
  choiceNotes,
  featureChips,
  featureOn,
  ideaChips,
  inlineKind,
  kindTitles,
  promptStyleNotes,
  selectedActions,
  splitProduct,
  textKeys,
  textKind,
  textLimits,
  textMissing,
  textPolicy,
  toggleAction,
  toggleFeature,
  toggleWish,
  unitFallback,
  verbs,
  wishChips,
  wishOn,
  wishesLimit,
  type TextKind,
} from './text-model';
import type { SourceMeta } from './text-media';
import { TextBox } from './TextBox';
import { TextForSegment, TextLineField, TextTemplates, TextToneRail } from './TextFormParts';
import { TextMediaFrame } from './TextMediaFrame';
import { TextProductTray } from './TextProductTray';
import { TextSettingsCard, type TextSettingItem } from './TextSettingsCard';
import { TextSttSource } from './TextSttSource';
import { TextTail } from './TextTail';
import { TextVariants, type VariantsSnapshot } from './TextVariants';
import { useTextDraft } from './use-text-draft';
import { useTextMore } from './use-text-more';
import './native-text.css';

const finished = (order: SpeechOrder | null) => !!order && (order.status === 'done' || order.status === 'failed');

export function TextOrderForm(props: { app: StudioApp; avatar: UserAvatar | null; initialInputs?: Record<string, unknown> }) {
  const state = useStudioForm(props);
  const draft = useTextDraft(state, !!props.initialInputs);
  const retry = useTailRetry(state);
  const [meta, setMetaState] = useState<Record<string, SourceMeta>>({});
  const [linkDraft, setLinkDraft] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [ownChoice, setOwnChoice] = useState<boolean | null>(null);
  const [kept, setKept] = useState<{ orders: SpeechOrder[]; snapshot: VariantsSnapshot } | null>(null);
  const setMeta = useCallback((key: string, value: SourceMeta | null) => {
    setMetaState(current => {
      const next = { ...current };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
  }, []);
  const { form, order, orderId, intent } = state;
  const policy = form ? textPolicy(form) : null;
  const keys = textKeys(form);
  /* После заказа форма показывает входы фактического заказа, а не правки поверх */
  const frozen = intent
    ? Object.fromEntries(Object.entries(intent.body.inputs).filter((entry): entry is [string, string] => typeof entry[1] === 'string'))
    : null;
  const inputs = orderId && frozen ? frozen : state.inputs;
  const kind: TextKind = policy ? textKind(policy, inputs, keys) : 'stt';
  const inline = inlineKind(kind);
  const more = useTextMore(state.ownerId, inline && order?.status === 'done' ? order : null, policy?.more);
  const draftBlocked = draft.blocked || draft.restoring;
  const done = finished(order);
  /* Варианты на месте: после готовых вариантов поля снова правятся — первая правка возвращает форму с тем же вводом */
  const editable = inline && !!orderId && done && !more.pending && !more.busy;
  const locked = (state.locked && !editable) || draftBlocked;
  const link = linkDraft ?? (keys.url ? state.inputs[keys.url] || '' : '');
  const snapshotOf = (values: Record<string, string>): VariantsSnapshot => ({
    kind: kind === 'pfix' ? 'pfix' : 'pnew',
    target: (keys.kind && values[keys.kind]) || 'photo',
    templates: selectedActions(keys.actions ? values[keys.actions] : undefined),
    own: !!(keys.wishes && values[keys.wishes]),
  });
  const setInputs = (change: SetStateAction<Record<string, string>>) => {
    if (editable) {
      setKept({ orders: [order!, ...more.orders], snapshot: snapshotOf(inputs) });
      void leaveTail(state).then(ok => ok && state.setInputs(change));
      return;
    }
    state.setInputs(change);
  };
  const write = (key: string | undefined, value: string | null) => {
    if (!key) return;
    setInputs(current => {
      const next = { ...current };
      if (value === null || value === '') delete next[key];
      else next[key] = value;
      return next;
    });
  };
  const view: CircleFormState = { ...state, inputs, setInputs, locked };

  /* Шапка: название экрана и цена за единицу подзаголовком — только из серверной цены (app.price / ToolPrice) */
  const price = toolPrice(props.app.price);
  const unit =
    kind === 'yt'
      ? unitText(typeof policy?.youtube_price === 'number' ? policy.youtube_price : null, unitFallback.yt)
      : unitText(price.amount, unitWords(price.unit, unitFallback[kind]));
  const title = policy ? kindTitles[kind] : props.app.name;
  const header = <ToolHeader title={title} unit={orderId && !inline ? undefined : unit || undefined} />;

  if (orderId && policy && kind !== 'pnew' && kind !== 'pfix')
    return (
      <section className="server-text st-in ap-in tt-in">
        {header}
        {state.error && <p role="alert">{uiText(state.error)}</p>}
        <TextTail state={state} kind={kind} keys={keys} meta={meta} onRetry={retry} />
      </section>
    );

  const missing = form ? textMissing(form, inputs, state.rights, { recording, link: keys.url ? link : undefined }) : 'Загружаем…';
  const hasSource = !!((keys.audio && inputs[keys.audio]) || (keys.video && inputs[keys.video]) || (keys.url && inputs[keys.url]));
  const expected = form ? studioExpectedPrice(form, inputs) : null;
  const perMinute = kind === 'stt' && price.unit === 'per_minute' && price.amount !== null;
  const buttonPrice = kind === 'stt' && !hasSource && perMinute ? price.amount : expected;
  const improve = policy?.improve_available === true;

  /* «Ещё настройки» по экрану (§1.5, состав — по таблице «Состав карточки») */
  const items: TextSettingItem[] = [];
  const choice = (
    key: string | undefined,
    title: string,
    icon: string,
    cell?: (value: string) => string,
    notes: Record<string, string> = choiceNotes,
    labels: Record<string, string> = choiceLabels,
  ) => {
    const field = key ? toolField(form, key) : undefined;
    if (!key || !field?.values.length) return;
    const current = inputs[key] ?? '';
    items.push({
      key,
      title,
      kind: 'choice',
      icon,
      value: (cell?.(current) ?? choiceLabels[current] ?? current) || 'Авто',
      current,
      options: field.values.map(value => [value, labels[value] ?? value, notes[value]]),
      set: value => write(key, value),
    });
  };
  const toggle = (key: string | undefined, title: string, icon: string, on: string, off: string, note: string) => {
    const field = key ? toolField(form, key) : undefined;
    if (!key || !field || (field.values.length && !(field.values.includes('true') && field.values.includes('false')))) return;
    const value = inputs[key] === 'true';
    items.push({ key, title, kind: 'toggle', icon, value: value ? on : off, on: value, note, wide: true, set: next => write(key, next) });
  };
  if (kind === 'stt') {
    toggle(keys.diarization, 'Кто говорит', 'users', 'Подписать собеседников', 'Без имён', 'Подписать собеседников');
    toggle(keys.timestamps, 'Время реплик', 'clock', 'В начале каждой реплики', 'Без времени', 'Время в начале каждой реплики');
    choice(keys.language, 'Язык записи', 'globe');
  } else if (kind === 'yt') {
    choice(keys.ytKind, 'Результат', 'doc');
    choice(keys.ytLang, 'Язык текста', 'globe', value => (value === 'src' ? 'Исходный' : (choiceLabels[value] ?? value)));
  } else if (kind === 'seo') {
    choice(keys.market, 'Площадка', 'bag', value => (value === 'ym' ? 'Я.Маркет' : (choiceLabels[value] ?? value)));
  } else if (kind === 'pphoto' || kind === 'pvideo') {
    const notes = Object.fromEntries(Object.entries(promptStyleNotes).map(([value, [, note]]) => [value, note]));
    const labels = Object.fromEntries(Object.entries(promptStyleNotes).map(([value, [label]]) => [value, label]));
    choice(keys.style, 'Стиль промпта', 'list', value => labels[value] ?? value, notes, labels);
    choice(keys.lang, 'Язык промпта', 'globe');
  }
  if (kind === 'seo' && keys.wishes && policy) {
    const limit = wishesLimit(policy, toolField(form, keys.wishes), 300);
    const value = inputs[keys.wishes] || '';
    items.push({
      key: keys.wishes,
      title: 'Пожелания',
      kind: 'text',
      icon: 'edit',
      value,
      empty: !value,
      wide: value.length > 24,
      set: next => write(keys.wishes, next),
      body: (
        <TextBox
          label="Пожелания"
          value={value}
          limit={limit}
          disabled={locked}
          placeholder="Например: коротко, простыми словами"
          improve={improve ? { kind: 'text' } : null}
          chips={wishChips.map(([label, phrase]) => ({
            label,
            on: wishOn(value, phrase),
            press: () => write(keys.wishes, Array.from(toggleWish(value, phrase)).slice(0, limit).join('')),
          }))}
          onChange={next => write(keys.wishes, next)}
        />
      ),
    });
  }

  /* Кнопка: «Получить текст · N C» (без источника — «от N C»), у вариантов на месте — «Ещё варианты · N C» и «Создаём» с крутилкой */
  const morePolicy = record(policy?.more);
  const running = !!orderId && !done;
  const moreReady = inline && !!orderId && order?.status === 'done' && morePolicy.available === true;
  const button = moreReady ? (
    <ToolCreateButton
      label="Ещё варианты"
      price={typeof morePolicy.expected_price === 'number' ? morePolicy.expected_price : null}
      chargeable={form?.chargeable}
      missing={null}
      busy={more.busy}
      blocked={more.blocked || state.submissionBlocked}
      pending={!!more.pending}
      available
      onCreate={() => void (more.pending ? more.retry() : more.request())}
    />
  ) : (
    <ToolCreateButton
      label={verbs[kind]}
      price={buttonPrice}
      from={kind === 'stt' && !hasSource && perMinute}
      chargeable={form?.chargeable}
      missing={inline && orderId && order?.status === 'failed' ? null : missing}
      busy={state.busy || (inline && running)}
      blocked={!form || draftBlocked || state.submissionBlocked || (!inline && !!orderId)}
      pending={!!intent && !orderId}
      available={!!form?.available}
      onCreate={() => {
        if (inline && orderId && order?.status === 'failed') retry();
        else void state.submit();
      }}
    />
  );

  /* «Создать промпт» и «Улучшить промпт» */
  const target = (keys.kind && inputs[keys.kind]) || toolField(form, keys.kind ?? '')?.values[0] || 'photo';
  const templates = (() => {
    const list = record(policy?.actions)[target];
    return Array.isArray(list) ? list.filter((value): value is string => typeof value === 'string') : [];
  })();
  const own = keys.wishes && inline ? (ownChoice ?? !!inputs[keys.wishes]) : null;
  const textField = toolField(form, keys.text ?? keys.features ?? '');
  const limits = policy ? textLimits(policy, textField, kind) : { min: 0, max: 500 };
  const productValue = keys.text ? inputs[keys.text] || '' : [inputs[keys.name ?? ''] || '', inputs[keys.features ?? ''] || ''].filter(Boolean).join('\n');
  const writeProduct = (value: string) => {
    if (keys.text) return write(keys.text, value);
    const { name, features } = splitProduct(value);
    setInputs(current => {
      const next = { ...current };
      if (keys.name) next[keys.name] = name;
      if (keys.features) next[keys.features] = features;
      return next;
    });
  };
  const variantOrders = orderId && inline && order ? [order, ...more.orders] : (kept?.orders ?? []);
  const variantSnapshot = orderId && inline ? snapshotOf(inputs) : kept?.snapshot;

  return (
    <section className="server-text st-in ap-in tt-in">
      {header}
      {state.error && <p role="alert">{uiText(state.error)}</p>}
      {draft.blocked && (
        <p role="alert">
          {uiText('Не удалось сохранить черновик. Создание заблокировано, чтобы не потерять данные.')}{' '}
          <button type="button" className="lnk" onClick={() => void draft.reset()}>
            {uiText('Сбросить сохранённый черновик')}
          </button>
        </p>
      )}
      {!form && !state.error && <p role="status">{uiText('Загружаем…')}</p>}
      {form && policy && (
        <form
          className="pf-body tt-body"
          data-tt-tool={kind}
          onSubmit={event => {
            event.preventDefault();
          }}
        >
          {(kind === 'stt' || kind === 'yt') && (
            <TextSttSource
              state={view}
              keys={keys}
              meta={meta}
              setMeta={setMeta}
              link={link}
              setLink={setLinkDraft}
              onRecording={setRecording}
            />
          )}
          {(kind === 'pphoto' || kind === 'pvideo') && (
            <TextMediaFrame
              state={view}
              fields={toolFields(form).filter(
                field =>
                  field.upload &&
                  (field.upload.kind === 'photo' ||
                    (field.upload.kind === 'video' && (policy.mode === 'prompt_from_video' || policy.prompt_from_video_available === true))),
              )}
              meta={meta}
              setMeta={setMeta}
              fromKey={keys.from}
              clipMaxMs={typeof policy.video_clip_max_ms === 'number' ? policy.video_clip_max_ms : 60000}
            />
          )}
          {kind === 'seo' && (
            <>
              {!!keys.photos.length && (
                <TextProductTray
                  state={view}
                  slots={keys.photos.map(key => toolField(form, key)!).slice(0, typeof policy.photo_max_count === 'number' ? Math.max(1, policy.photo_max_count) : 5)}
                />
              )}
              {keys.tone && !!toolField(form, keys.tone)?.values.length && (
                <TextToneRail values={toolField(form, keys.tone)!.values} selected={inputs[keys.tone] || ''} disabled={locked} onPick={value => write(keys.tone, value)} />
              )}
              {(keys.text || keys.name) && (
                <TextBox
                  label="Название и преимущества"
                  value={productValue}
                  limit={limits.max}
                  disabled={locked}
                  placeholder={'Флисовая куртка женская на молнии\nПлотный мягкий флис\nДва боковых кармана'}
                  note="Название первой строкой, ниже по преимуществу"
                  improve={improve ? { kind: 'text' } : null}
                  chips={featureChips.map(label => ({
                    label,
                    on: featureOn(productValue, uiText(label)),
                    press: () => writeProduct(Array.from(toggleFeature(productValue, uiText(label))).slice(0, limits.max).join('')),
                  }))}
                  onChange={writeProduct}
                />
              )}
            </>
          )}
          {inline && (
            <>
              {keys.kind && !!toolField(form, keys.kind)?.values.length && (
                <TextForSegment
                  values={toolField(form, keys.kind)!.values}
                  selected={target}
                  disabled={locked}
                  onPick={value =>
                    setInputs(current => ({ ...current, [keys.kind!]: value, ...(keys.actions ? { [keys.actions]: '[]' } : {}) }))
                  }
                />
              )}
              <TextTemplates
                label={kind === 'pnew' ? 'Жанр или стиль' : 'Что сделать'}
                templates={keys.actions ? templates : []}
                selected={selectedActions(keys.actions ? inputs[keys.actions] : undefined)}
                own={own}
                disabled={locked}
                onTemplate={value => keys.actions && setInputs(current => ({ ...current, [keys.actions!]: toggleAction(current[keys.actions!], value) }))}
                onOwn={() => {
                  setOwnChoice(!own);
                  if (own) write(keys.wishes, null);
                }}
              />
              {own && keys.wishes && (
                <TextLineField
                  label="Что именно нужно"
                  value={inputs[keys.wishes] || ''}
                  limit={wishesLimit(policy, toolField(form, keys.wishes))}
                  placeholder="Например: вечер, дождь, неон"
                  disabled={locked}
                  onChange={value => write(keys.wishes, value)}
                />
              )}
              {keys.text && (
                <TextBox
                  label={kind === 'pnew' ? 'Идея' : 'Ваш промпт'}
                  value={inputs[keys.text] || ''}
                  limit={limits.max}
                  minimum={limits.min}
                  disabled={locked}
                  placeholder={kind === 'pnew' ? 'О чём промпт? Например: кот-астронавт сидит на Луне и смотрит на Землю' : 'Вставьте промпт, который хотите улучшить'}
                  note="Политику и запрещённые темы не пропустим"
                  chips={
                    kind === 'pnew'
                      ? ideaChips.map(([label, idea]) => ({
                          label,
                          on: (inputs[keys.text!] || '').trim() === uiText(idea),
                          press: () => write(keys.text, (inputs[keys.text!] || '').trim() === uiText(idea) ? null : uiText(idea)),
                        }))
                      : []
                  }
                  onChange={value => write(keys.text, value)}
                />
              )}
              {variantSnapshot && <TextVariants orders={variantOrders} owner={state.ownerId} snapshot={variantSnapshot} />}
              {more.error && (
                <p className="note warn" role="alert">
                  {uiText(more.error)}
                </p>
              )}
            </>
          )}
          {!inline && <TextSettingsCard items={items} disabled={locked} />}
          {button}
        </form>
      )}
    </section>
  );
}
